import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'

/** Props for the useContentEditorFocus hook. */
interface UseContentEditorFocusProps {
    /** The ID of the content item to be focused. */
    contentItemId?: string
    /** The optional handler to activate CKEditor. */
    activate?: () => void
}

/**
 * Custom hook that handles setting focus on `editingTextContentElement` by matching its id.
 *
 * @param {UseContentEditorFocusProps} props - Props for the hook.
 * @param {string} props.contentItemId - The ID of the content item to be focused.
 * @param {object} props.activate - The optional handler to activate CKEditor.
 * @returns The CKEditor (or type of T) ref to focus with.
 */
export const useNewContentFocus = <T extends { focus: () => void }>({
    contentItemId,
    activate,
}: UseContentEditorFocusProps) => {
    const editorFocusRef: MutableRefObject<T | null> = useRef(null)
    const editingTextContentElement = useShallowSelector(
        (state) => state.textContentElementToAdd
    )
    const ckeToggle = useCKEditorToggle(contentItemId)
    const registerNewEditor = useCallback((editor: T) => {
        editorFocusRef.current = editor
    }, [])

    //deFocus the editor when there add operation started
    const unFocusEditor = useCallback(() => {
        if (editorFocusRef?.current) {
            editorFocusRef.current = null
            ckeToggle.deactivate()
        }
    }, [])

    useEffect(() => {
        if (editingTextContentElement?.id === contentItemId) {
            activate?.()

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    editorFocusRef.current?.focus?.()
                })
            })
        }
    }, [editingTextContentElement, activate, contentItemId])

    return { unFocusEditor, registerNewEditor }
}
